import { ReadonlyVec2, ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import { gl } from '~/engine/Engine';
import { RenderStorage } from '~/engine/core/render/RenderStorage';
import { FrameBufferObject } from '~/engine/core/utils/FrameBufferObject';
import {
    RegionOfInterest,
    TextureWrite,
} from '~/engine/data/structure/RegionOfInterest';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { ViewManager } from '~/engine/view/ViewManager';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import {
    getBrushSigmaMm,
    SmoothBrushRenderer,
} from '~/engine/util/algorithms/sdf/SmoothBrushRenderer';
import { sdfSurfaceWithinRange } from '~/engine/util/algorithms/sdf/sdfSurfaceProximity';
import { createSdfTexture3D } from '~/engine/util/algorithms/sdf/Texture3DUtils';
import {
    clampBoxToGrid,
    intersectWithSliceSlab,
    isEmptyBox,
    VoxelBox,
} from '~/engine/util/algorithms/sdf/voxelBox';
import { StrokeMode } from '~/state/atoms/toolsAtoms';

/**
 * Mutable state for an active smoothing-brush stroke.
 *
 * Unlike the deform stroke — an idempotent warp re-rendered from the
 * stroke-start snapshot every frame — smoothing ACCUMULATES: each
 * rAF-coalesced pass blurs the live SDF in place within the brush
 * footprint (see {@link SmoothBrushRenderer}), so dragging (or
 * re-crossing) a region smooths it progressively. Voxels outside the
 * brush ball are untouched by construction (blend weight 0), so no
 * previous-frame restore range is needed.
 *
 * The CPU snapshot taken at stroke start exists solely for cancel
 * (Escape) and the local surface-proximity check; on finish the
 * affected slices are read back from the GPU and committed as one
 * undo entry via {@link TextureWrite}.
 */
export class SmoothBrushActionState {
    private readonly structure: RegionOfInterest;
    private readonly view: PlaneView;
    private readonly brushRadiusMm: number;
    private readonly sigmaMm: number;
    private readonly mode: StrokeMode;
    private readonly textureWrite: TextureWrite;

    /** Texture grid size in voxels (x, y, z). */
    private readonly size: vec3;
    /** Texture grid resolution in mm/voxel. */
    private readonly res: vec3;

    /**
     * CPU snapshot of the SDF at stroke start; used by the surface
     * search and to restore slices on cancel.
     */
    private originalSlices: Uint8Array[] | undefined;

    /** Full-volume R8 scratch textures for the blur/blend passes. */
    private scratchA: WebGLTexture | undefined;
    private scratchB: WebGLTexture | undefined;
    /** FBO used by the passes and the region copy-back. */
    private fbo: FrameBufferObject | undefined;

    /** Current patient mouse position (mm). */
    private readonly currentMouse: vec3;

    /** Coalesces mouse events into one pass per animation frame. */
    private rafPending = false;

    /**
     * Z indices of every slice modified during the stroke; resynced
     * on finish, restored on cancel.
     */
    private readonly affectedSlices: Set<number> = new Set();

    public constructor(
        structure: RegionOfInterest,
        view: PlaneView,
        startCoordinates: ReadonlyVec2,
        diameterMm: number,
        strength: number,
        mode: StrokeMode
    ) {
        this.structure = structure;
        this.view = view;
        this.brushRadiusMm = diameterMm / 2;
        this.sigmaMm = getBrushSigmaMm(strength);
        this.mode = mode;

        this.currentMouse = vec3.clone(
            view.clientToPatientCoordinates(startCoordinates)
        );

        this.textureWrite = structure.startModify('smooth brush');

        this.size = vec3.create();
        this.res = vec3.fromValues(1, 1, 1);

        try {
            const texture = structure.texture;
            if (texture && !texture.isEmpty) {
                const grid = texture.textureGrid;
                vec3.set(this.size, grid.sizeX, grid.sizeY, grid.sizeZ);
                vec3.set(this.res, grid.resX, grid.resY, grid.resZ);

                // The CPU cache is populated by startModify, so
                // reading `pixels` triggers no GPU readback.
                const sdfPixels = texture.signedDistanceField.pixels;
                this.originalSlices = sdfPixels.map((s) => new Uint8Array(s));

                const scratchA = createSdfTexture3D(
                    this.size[0],
                    this.size[1],
                    this.size[2]
                );
                if (!scratchA) {
                    throw new Error(
                        'Failed to allocate smoothing scratch texture'
                    );
                }
                this.scratchA = scratchA;
                const scratchB = createSdfTexture3D(
                    this.size[0],
                    this.size[1],
                    this.size[2]
                );
                if (!scratchB) {
                    throw new Error(
                        'Failed to allocate smoothing scratch texture'
                    );
                }
                this.scratchB = scratchB;
                this.fbo = new FrameBufferObject(
                    vec2.fromValues(this.size[0], this.size[1])
                );
                this.fbo.unbind();
            }
        } catch (error) {
            // Nothing has been written yet, so cancelling leaves the
            // ROI unchanged and unlocked.
            this.disposeGpuResources();
            this.textureWrite.cancel();
            throw error;
        }
    }

    public get targetStructure(): RegionOfInterest {
        return this.structure;
    }

    /**
     * Move the brush and schedule a smoothing pass for the next
     * animation frame. Also used for the initial press position, so a
     * click without movement applies exactly one pass.
     */
    public onMouseMove(coordinates: ReadonlyVec2): EventHandlerResult {
        vec3.copy(
            this.currentMouse,
            this.view.clientToPatientCoordinates(coordinates)
        );
        if (this.rafPending) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        this.rafPending = true;
        requestAnimationFrame(() => {
            if (!this.rafPending) {
                return;
            }
            this.rafPending = false;
            this.applySmoothingPass();
            // One invalidation, no direct `renderLayers()`: the scheduler
            // coalesces this into the next frame's repaint, so a stroke that
            // outruns the frame rate paints once per frame rather than once
            // per pass.
            ViewManager.invalidate(
                LayerBit.CONTOUR_LAYER_BIT,
                this.structure.boundingBox
            );
        });
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * Commit the smoothed SDF: read the affected slices back into the
     * CPU pixel cache, then clip the result to the image volume.
     */
    public finish(): void {
        this.flushPendingFrame();
        const texture = this.structure.texture;
        if (texture && !texture.isEmpty && this.affectedSlices.size > 0) {
            const sdf = texture.signedDistanceField;
            for (const z of this.affectedSlices) {
                sdf.syncSliceFromGpu(z, 2);
            }
            texture.cropToImageBounds();
        }
        this.disposeGpuResources();
        this.textureWrite.finish();
    }

    /**
     * Restore the original SDF (Escape): reupload the affected slices
     * to the GPU from the snapshot.
     */
    public cancel(): void {
        this.rafPending = false;
        const texture = this.structure.texture;
        if (
            texture &&
            !texture.isEmpty &&
            this.originalSlices &&
            this.affectedSlices.size > 0
        ) {
            const sdf = texture.signedDistanceField;
            for (const z of this.affectedSlices) {
                sdf.uploadSliceToGpu(this.originalSlices[z], z);
            }
        }
        this.disposeGpuResources();
        this.textureWrite.cancel();
    }

    /**
     * If a frame is pending, run the pass synchronously so the final
     * state is committed before the stroke ends.
     */
    private flushPendingFrame(): void {
        if (this.rafPending) {
            this.rafPending = false;
            this.applySmoothingPass();
        }
    }

    private disposeGpuResources(): void {
        if (this.scratchA) {
            gl.deleteTexture(this.scratchA);
            this.scratchA = undefined;
        }
        if (this.scratchB) {
            gl.deleteTexture(this.scratchB);
            this.scratchB = undefined;
        }
        this.fbo?.delete();
        this.fbo = undefined;
    }

    /**
     * Whether the structure surface lies within the brush ball,
     * measured on the pre-stroke SDF snapshot. Smoothing a region with
     * no surface is a no-op, so passes far from the structure are
     * skipped entirely.
     */
    private surfaceWithinBrush(centerMm: ReadonlyVec3): boolean {
        const texture = this.structure.texture;
        if (!this.originalSlices || !texture || texture.isEmpty) {
            return false;
        }
        return sdfSurfaceWithinRange(
            this.originalSlices,
            this.size,
            this.res,
            texture.textureGrid.toVoxelSpace(centerMm),
            this.brushRadiusMm,
            texture.margin
        );
    }

    /**
     * Render one smoothing pass at the current cursor position,
     * restricted to the brush ball's voxel bounding box (intersected
     * with the cursor's slice slab in 2D mode).
     */
    private applySmoothingPass(): void {
        if (!this.scratchA || !this.scratchB || !this.fbo) {
            return;
        }
        const texture = this.structure.texture;
        if (!texture || texture.isEmpty) {
            return;
        }
        if (!this.surfaceWithinBrush(this.currentMouse)) {
            return;
        }
        const grid = texture.textureGrid;
        const mouseVoxel = grid.toVoxelSpace(this.currentMouse);

        // The brush ball's voxel bounding box, confined to the cursor's
        // slice in 2D mode.
        const brushBox: VoxelBox = {
            xMin: Math.floor(mouseVoxel[0] - this.brushRadiusMm / this.res[0]),
            xMax: Math.ceil(mouseVoxel[0] + this.brushRadiusMm / this.res[0]),
            yMin: Math.floor(mouseVoxel[1] - this.brushRadiusMm / this.res[1]),
            yMax: Math.ceil(mouseVoxel[1] + this.brushRadiusMm / this.res[1]),
            zMin: Math.floor(mouseVoxel[2] - this.brushRadiusMm / this.res[2]),
            zMax: Math.ceil(mouseVoxel[2] + this.brushRadiusMm / this.res[2]),
        };
        const box = clampBoxToGrid(
            this.mode === '2d'
                ? intersectWithSliceSlab(
                      brushBox,
                      mouseVoxel,
                      this.view.viewDirection,
                      grid
                  )
                : brushBox,
            this.size
        );
        if (isEmptyBox(box)) {
            return;
        }

        const sdf = texture.signedDistanceField;
        const renderer = RenderStorage.getRenderer(SmoothBrushRenderer);
        renderer.renderBrushPass({
            liveTex: sdf.texture,
            scratchA: this.scratchA,
            scratchB: this.scratchB,
            fbo: this.fbo,
            texSize: this.size,
            resMmPerVoxel: this.res,
            voxelToWorld: grid.voxelToWorld,
            mouseMm: this.currentMouse,
            brushRadiusMm: this.brushRadiusMm,
            sigmaMm: this.sigmaMm,
            mode: this.mode,
            viewDirection: this.view.viewDirection,
            slabThicknessMm: grid.voxelExtentAlong(this.view.viewDirection),
            box,
        });

        for (let z = box.zMin; z <= box.zMax; z++) {
            this.affectedSlices.add(z);
        }
    }
}

/**
 * Manages the lifecycle of a smoothing-brush stroke.
 */
export class SmoothBrushAction {
    private readonly structure: RegionOfInterest;
    private readonly diameterMm: number;
    private readonly strength: number;
    private readonly mode: StrokeMode;

    public constructor(
        structure: RegionOfInterest,
        diameterMm: number,
        strength: number,
        mode: StrokeMode
    ) {
        this.structure = structure;
        this.diameterMm = diameterMm;
        this.strength = strength;
        this.mode = mode;
    }

    public startAction(
        view: PlaneView,
        coordinates: ReadonlyVec2
    ): SmoothBrushActionState {
        const state = new SmoothBrushActionState(
            this.structure,
            view,
            coordinates,
            this.diameterMm,
            this.strength,
            this.mode
        );
        // A click without movement applies exactly one pass.
        state.onMouseMove(coordinates);
        return state;
    }

    public stopAction(state: SmoothBrushActionState): EventHandlerResult {
        state.finish();
        const bb = state.targetStructure.boundingBox;
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            { layers: LayerBit.CONTOUR_LAYER_BIT, volume: bb }
        );
    }
}
